import { apiClient } from '../../services/apiClient';
import type { CollectibleBadge, BadgeCode } from '../badges/badgeApi';

export type ProgressSortField = 'name' | 'totalPoints' | 'completionPct' | 'lastActivity';

export interface StudentProgress {
  userId: string;
  name: string;
  totalPoints: number;
  completionPct: number;
  lastActivity: string | null;
}

export interface SubmissionRecord {
  id: string;
  exerciseId: string;
  exerciseTitle: string;
  submittedCommand: string | null;
  executionOutput: string | null;
  passed: boolean;
  pointsEarned: number;
  error: string | null;
  createdAt: string;
}

/** Builds the `sort` query value from a field and direction, e.g. `-totalPoints` for descending. */
export function toSortParam(field: ProgressSortField, descending: boolean): string {
  return `${descending ? '-' : ''}${field}`;
}

export function listCourseProgress(
  courseId: string,
  sort?: string,
  filter?: string,
  chapterId?: string,
): Promise<StudentProgress[]> {
  const params = new URLSearchParams();
  if (sort) {
    params.set('sort', sort);
  }
  if (filter) {
    params.set('filter', filter);
  }
  if (chapterId) {
    params.set('chapterId', chapterId);
  }
  const query = params.toString();
  return apiClient(`/courses/${courseId}/students/progress${query ? `?${query}` : ''}`);
}

export function listStudentSubmissionsInCourse(
  courseId: string,
  userId: string,
  chapterId?: string,
): Promise<SubmissionRecord[]> {
  const query = chapterId ? `?${new URLSearchParams({ chapterId })}` : '';
  return apiClient(`/courses/${courseId}/students/${userId}/submissions${query}`);
}

export function listExerciseSubmissions(
  exerciseId: string,
  userId: string,
): Promise<SubmissionRecord[]> {
  const params = new URLSearchParams({ userId });
  return apiClient(`/exercises/${exerciseId}/submissions?${params.toString()}`);
}

export interface ChapterCompletion {
  chapterId: string;
  chapterTitle: string;
  completionPct: number;
}

export interface CourseProgress {
  totalPoints: number;
  completionPctByChapter: ChapterCompletion[];
  achievements: string[];
  badgeCollectionConsented?: boolean;
  collectibleBadges?: CollectibleBadge[];
  selectedBadgeCode?: BadgeCode | null;
}

/** Fetches the authenticated student's own progress/achievements for a course (FR-027, US5). */
export function getOwnProgress(courseId: string): Promise<CourseProgress> {
  return apiClient(`/courses/${courseId}/progress`);
}
